import React, { useState, useEffect } from 'react';
import { Lock, ShieldCheck, User, Mail, ArrowLeft, Eye, EyeOff, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { GoogleLogin } from '@react-oauth/google';

const LoginPage = ({ onLogin, onBackToLanding, initialMode }) => {
    const [isRegister, setIsRegister] = useState(initialMode === 'register');
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [resetStep, setResetStep] = useState(1); // 1 = request code, 2 = verify code & reset
    const [recoveryMethod, setRecoveryMethod] = useState('email'); // 'email' | 'whatsapp'
    const [recoveryChannels, setRecoveryChannels] = useState({ email: true, whatsapp: true });
    const [maskedRecipient, setMaskedRecipient] = useState('');
    
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [password, setPassword] = useState('');
    const [resetIdentifier, setResetIdentifier] = useState('');
    const [resetCode, setResetCode] = useState('');
    
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [isRegisterVerification, setIsRegisterVerification] = useState(false);
    const [registerCode, setRegisterCode] = useState('');

    useEffect(() => {
        if (isForgotPassword) {
            api.get('/auth/recovery-channels')
                .then(res => {
                    if (res.data) {
                        setRecoveryChannels(res.data);
                        if (res.data.email && !res.data.whatsapp) {
                            setRecoveryMethod('email');
                        } else if (res.data.whatsapp && !res.data.email) {
                            setRecoveryMethod('whatsapp');
                        }
                    }
                })
                .catch(() => {});
        }
    }, [isForgotPassword]);

    useEffect(() => {
        if (initialMode) {
            setIsRegister(initialMode === 'register');
            setIsForgotPassword(false);
        }
    }, [initialMode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isRegister) {
            if (!username.trim() || !email.trim() || !whatsapp.trim() || !password.trim()) {
                return toast.error("Por favor, preencha todos os campos.");
            }

            setLoading(true);
            try {
                const response = await api.post('/auth/register', { 
                    username: username.trim(), 
                    email: email.trim(), 
                    password: password.trim() 
                });

                if (response.data.success) {
                    localStorage.setItem('is_logged_in', 'true');
                    // token é gerenciado via cookie HttpOnly
                    
                    try {
                        await api.post('/auth/whatsapp/send-code', { whatsappNumber: whatsapp.trim() });
                        toast.success(`Conta criada! Enviamos um código para seu WhatsApp.`);
                        setIsRegisterVerification(true);
                    } catch (codeErr) {
                        toast.error(codeErr.response?.data?.error || "Conta criada, mas erro ao enviar WhatsApp. Você pode verificar depois.");
                        onLogin();
                    }
                }
            } catch (err) {
                const message = err.response?.data?.error || "Erro ao realizar cadastro.";
                toast.error(message);
            } finally {
                setLoading(false);
            }
        } else {
            if (!username.trim() || !password.trim()) {
                return toast.error("Por favor, preencha todos os campos.");
            }

            setLoading(true);
            try {
                const response = await api.post('/auth/login', { 
                    username: username.trim(), 
                    password: password.trim() 
                });

                if (response.data.success) {
                    localStorage.setItem('is_logged_in', 'true');
                    // token é gerenciado via cookie HttpOnly
                    onLogin();
                    toast.success(`Bem-vindo, ${username}!`);
                }
            } catch (err) {
                const message = err.response?.data?.error || "Erro ao realizar login.";
                toast.error(message);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        setLoading(true);
        try {
            const response = await api.post('/auth/google', {
                credential: credentialResponse.credential
            });
            
            if (response.data.success) {
                localStorage.setItem('is_logged_in', 'true');
                // token é gerenciado via cookie HttpOnly
                onLogin();
                toast.success(`Bem-vindo, ${response.data.user.name}!`);
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro no login pelo Google.");
        } finally {
            setLoading(false);
        }
    };

    const handleRequestResetCode = async (e) => {
        e.preventDefault();
        if (!resetIdentifier.trim()) return toast.error("Preencha seu usuário, email ou whatsapp.");
        
        setLoading(true);
        try {
            const res = await api.post('/auth/forgot-password', { 
                identifier: resetIdentifier.trim(),
                method: recoveryMethod
            });
            const msg = res.data?.message || (recoveryMethod === 'email' ? "Código enviado para o seu e-mail!" : "Código enviado para o seu WhatsApp!");
            toast.success(msg);
            if (res.data?.targetMasked) {
                setMaskedRecipient(res.data.targetMasked);
            }
            if (res.data?.method) {
                setRecoveryMethod(res.data.method);
            }
            setResetStep(2);
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao solicitar código.");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!resetCode.trim() || !password.trim()) return toast.error("Preencha o código e a nova senha.");
        
        setLoading(true);
        try {
            await api.post('/auth/reset-password', { 
                identifier: resetIdentifier.trim(),
                code: resetCode.trim(),
                newPassword: password.trim()
            });
            toast.success("Senha alterada com sucesso! Faça login.");
            setIsForgotPassword(false);
            setResetStep(1);
            setPassword('');
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao redefinir a senha.");
        } finally {
            setLoading(false);
        }
    };

    if (isRegisterVerification) {
        return (
            <div style={containerStyle}>
                <div style={cardStyle}>
                    <div style={headerStyle}>
                        <div style={iconBgStyle}>
                            <MessageSquare size={32} color="#ee4d2d" />
                        </div>
                        <h2 style={titleStyle}>Verifique seu WhatsApp</h2>
                        <p style={subtitleStyle}>
                            Enviamos um código de 6 dígitos para <strong>{whatsapp}</strong>.
                        </p>
                    </div>

                    <form onSubmit={async (e) => {
                        e.preventDefault();
                        if (!registerCode) return toast.error("Digite o código recebido.");
                        setLoading(true);
                        try {
                            await api.post('/auth/whatsapp/verify', { code: registerCode });
                            toast.success("WhatsApp verificado com sucesso!");
                            onLogin();
                        } catch (err) {
                            toast.error(err.response?.data?.error || "Código inválido.");
                        } finally {
                            setLoading(false);
                        }
                    }} style={formStyle}>
                        <div style={inputGroupStyle}>
                            <label style={labelStyle}>Código de 6 dígitos</label>
                            <div style={inputWrapperStyle}>
                                <MessageSquare size={18} color="#94a3b8" style={{ marginLeft: '12px' }} />
                                <input
                                    type="text"
                                    placeholder="000000"
                                    value={registerCode}
                                    onChange={(e) => setRegisterCode(e.target.value)}
                                    style={inputStyle}
                                    disabled={loading}
                                    maxLength="6"
                                />
                            </div>
                        </div>
                        <button type="submit" style={buttonStyle} disabled={loading}>
                            {loading ? 'VERIFICANDO...' : 'CONFIRMAR E ENTRAR'}
                        </button>
                    </form>

                    <div style={{ textAlign: 'center', marginTop: '-10px' }}>
                        <button
                            type="button"
                            onClick={() => onLogin()}
                            style={{
                                background: 'none', border: 'none', color: '#64748b', fontSize: '13px',
                                fontWeight: '600', cursor: 'pointer', textDecoration: 'underline'
                            }}
                        >
                            Pular por enquanto (verificar depois)
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (isForgotPassword) {
        return (
            <div style={containerStyle}>
                <div style={cardStyle}>
                    <button
                        onClick={() => {
                            setIsForgotPassword(false);
                            setResetStep(1);
                        }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px', background: 'none',
                            border: 'none', color: '#64748b', fontSize: '13px', fontWeight: '700',
                            cursor: 'pointer', padding: 0, alignSelf: 'flex-start', transition: 'color 0.2s'
                        }}
                    >
                        <ArrowLeft size={16} /> Voltar para o login
                    </button>
                    
                    <div style={headerStyle}>
                        <div style={iconBgStyle}>
                            {recoveryMethod === 'email' ? <Mail size={32} color="#ee4d2d" /> : <MessageSquare size={32} color="#ee4d2d" />}
                        </div>
                        <h2 style={titleStyle}>Recuperar Senha</h2>
                        <p style={subtitleStyle}>
                            {resetStep === 1 
                                ? (recoveryMethod === 'email' ? 'Enviaremos um código para o seu e-mail cadastrado' : 'Enviaremos um código via WhatsApp para você') 
                                : (recoveryMethod === 'email' 
                                    ? `Digite o código enviado para ${maskedRecipient || 'seu e-mail'} e sua nova senha`
                                    : `Digite o código recebido no WhatsApp ${maskedRecipient ? `(${maskedRecipient})` : ''} e sua nova senha`)}
                        </p>
                    </div>

                    {resetStep === 1 ? (
                        <form onSubmit={handleRequestResetCode} style={formStyle}>
                            {recoveryChannels.email && recoveryChannels.whatsapp && (
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setRecoveryMethod('email')}
                                        style={{
                                            flex: 1,
                                            padding: '10px 12px',
                                            borderRadius: '10px',
                                            border: recoveryMethod === 'email' ? '2px solid #ee4d2d' : '1px solid #e2e8f0',
                                            backgroundColor: recoveryMethod === 'email' ? '#fff5f3' : '#f8fafc',
                                            color: recoveryMethod === 'email' ? '#ee4d2d' : '#64748b',
                                            fontWeight: '700',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <Mail size={15} /> Por E-mail
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setRecoveryMethod('whatsapp')}
                                        style={{
                                            flex: 1,
                                            padding: '10px 12px',
                                            borderRadius: '10px',
                                            border: recoveryMethod === 'whatsapp' ? '2px solid #25d366' : '1px solid #e2e8f0',
                                            backgroundColor: recoveryMethod === 'whatsapp' ? '#f0fdf4' : '#f8fafc',
                                            color: recoveryMethod === 'whatsapp' ? '#16a34a' : '#64748b',
                                            fontWeight: '700',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <MessageSquare size={15} /> Por WhatsApp
                                    </button>
                                </div>
                            )}

                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Usuário, E-mail ou WhatsApp</label>
                                <div style={inputWrapperStyle}>
                                    <User size={18} color="#94a3b8" style={{ marginLeft: '12px' }} />
                                    <input
                                        type="text"
                                        placeholder="Ex: seuemail@... ou usuario"
                                        value={resetIdentifier}
                                        onChange={(e) => setResetIdentifier(e.target.value)}
                                        style={inputStyle}
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                            <button type="submit" style={buttonStyle} disabled={loading}>
                                {loading ? 'ENVIANDO...' : (recoveryMethod === 'email' ? 'ENVIAR CÓDIGO POR E-MAIL' : 'ENVIAR CÓDIGO POR WHATSAPP')}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleResetPassword} style={formStyle}>
                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Código Recebido ({recoveryMethod === 'email' ? 'E-mail' : 'WhatsApp'})</label>
                                <div style={inputWrapperStyle}>
                                    <MessageSquare size={18} color="#94a3b8" style={{ marginLeft: '12px' }} />
                                    <input
                                        type="text"
                                        placeholder="Código de 6 dígitos"
                                        value={resetCode}
                                        onChange={(e) => setResetCode(e.target.value)}
                                        style={inputStyle}
                                        disabled={loading}
                                        maxLength="6"
                                    />
                                </div>
                            </div>
                            <div style={inputGroupStyle}>
                                <label style={labelStyle}>Nova Senha</label>
                                <div style={inputWrapperStyle}>
                                    <Lock size={18} color="#94a3b8" style={{ marginLeft: '12px' }} />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Sua nova senha secreta"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        style={inputStyle}
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{ background: 'none', border: 'none', padding: '0 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', outline: 'none' }}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                            <button type="submit" style={buttonStyle} disabled={loading}>
                                {loading ? 'PROCESSANDO...' : 'REDEFINIR SENHA'}
                            </button>

                            <div style={{ textAlign: 'center', marginTop: '10px' }}>
                                <button
                                    type="button"
                                    onClick={() => setResetStep(1)}
                                    style={{
                                        background: 'none', border: 'none', color: '#64748b', fontSize: '13px',
                                        fontWeight: '600', cursor: 'pointer', textDecoration: 'underline'
                                    }}
                                >
                                    Não recebeu o código? Tentar outro método ou reenviar
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            <div style={cardStyle}>
                {onBackToLanding && (
                    <button
                        onClick={onBackToLanding}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px', background: 'none',
                            border: 'none', color: '#64748b', fontSize: '13px', fontWeight: '700',
                            cursor: 'pointer', padding: 0, alignSelf: 'flex-start', transition: 'color 0.2s'
                        }}
                    >
                        <ArrowLeft size={16} /> Voltar para o início
                    </button>
                )}
                <div style={headerStyle}>
                    <div style={iconBgStyle}>
                        <ShieldCheck size={32} color="#ee4d2d" />
                    </div>
                    <h2 style={titleStyle}>{isRegister ? 'Criar Nova Conta' : 'Painel de Automação'}</h2>
                    <p style={subtitleStyle}>
                        {isRegister ? 'Cadastre-se grátis para começar' : 'Entre com suas credenciais para continuar'}
                    </p>
                </div>

                <form onSubmit={handleSubmit} style={formStyle}>
                    <div style={inputGroupStyle}>
                        <label style={labelStyle}>Nome de Usuário</label>
                        <div style={inputWrapperStyle}>
                            <User size={18} color="#94a3b8" style={{ marginLeft: '12px' }} />
                            <input
                                type="text"
                                placeholder="Nome de usuário"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                style={inputStyle}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    {isRegister && (
                        <div style={inputGroupStyle}>
                            <label style={labelStyle}>Email</label>
                            <div style={inputWrapperStyle}>
                                <Mail size={18} color="#94a3b8" style={{ marginLeft: '12px' }} />
                                <input
                                    type="email"
                                    placeholder="seuemail@exemplo.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    style={inputStyle}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    )}

                    {isRegister && (
                        <div style={inputGroupStyle}>
                            <label style={labelStyle}>WhatsApp (com DDD)</label>
                            <div style={inputWrapperStyle}>
                                <MessageSquare size={18} color="#94a3b8" style={{ marginLeft: '12px' }} />
                                <input
                                    type="text"
                                    placeholder="Ex: 11999999999"
                                    value={whatsapp}
                                    onChange={(e) => setWhatsapp(e.target.value)}
                                    style={inputStyle}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    )}

                    <div style={inputGroupStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label style={labelStyle}>Senha</label>
                            {!isRegister && (
                                <button 
                                    type="button" 
                                    onClick={() => setIsForgotPassword(true)}
                                    style={{ background: 'none', border: 'none', color: '#ee4d2d', fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0 }}
                                >
                                    Esqueceu a senha?
                                </button>
                            )}
                        </div>
                        <div style={inputWrapperStyle}>
                            <Lock size={18} color="#94a3b8" style={{ marginLeft: '12px' }} />
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Sua senha secreta"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                style={inputStyle}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{ background: 'none', border: 'none', padding: '0 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', outline: 'none' }}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <button type="submit" style={buttonStyle} disabled={loading}>
                        {loading ? 'PROCESSANDO...' : (isRegister ? 'CRIAR CONTA GRÁTIS' : 'ENTRAR NO SISTEMA')}
                    </button>
                </form>

                <div style={{ textAlign: 'center', marginTop: '-10px' }}>
                    <button
                        type="button"
                        onClick={() => {
                            setIsRegister(!isRegister);
                            setUsername('');
                            setEmail('');
                            setPassword('');
                        }}
                        style={{
                            background: 'none', border: 'none', color: '#ee4d2d', fontSize: '14px',
                            fontWeight: '800', cursor: 'pointer', textDecoration: 'underline'
                        }}
                    >
                        {isRegister ? 'Já tenho uma conta. Fazer Login' : 'Ainda não tem conta? Cadastre-se grátis!'}
                    </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', margin: '5px 0' }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }} />
                    <span style={{ margin: '0 10px', color: '#94a3b8', fontSize: '13px', fontWeight: 'bold' }}>OU</span>
                    <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }} />
                </div>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => toast.error('Falha ao conectar com o Google')}
                        shape="pill"
                        size="large"
                        text="continue_with"
                        width="300"
                    />
                </div>

                <div style={footerStyle}>
                    <p>🔒 Acesso Restrito e Criptografado</p>
                </div>
            </div>
        </div>
    );
};

// Estilos Premium
const containerStyle = {
    minHeight: '100vh', width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    padding: '35px 20px', boxSizing: 'border-box'
};

const cardStyle = {
    width: '100%', maxWidth: '550px', backgroundColor: '#fff', borderRadius: '24px', padding: '30px', boxSizing: 'border-box',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    display: 'flex', flexDirection: 'column', gap: '22px', margin: 'auto'
};

const headerStyle = { textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' };
const iconBgStyle = { width: '64px', height: '64px', borderRadius: '16px', backgroundColor: '#fff1f0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' };
const titleStyle = { fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 };
const subtitleStyle = { fontSize: '14px', color: '#64748b', margin: 0 };
const formStyle = { display: 'flex', flexDirection: 'column', gap: '20px' };
const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: '8px' };
const labelStyle = { fontSize: '13px', fontWeight: '700', color: '#475569', marginLeft: '4px' };
const inputWrapperStyle = { display: 'flex', alignItems: 'center', backgroundColor: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: '12px', transition: '0.2s' };
const inputStyle = { flex: 1, padding: '14px 12px', border: 'none', backgroundColor: 'transparent', fontSize: '15px', outline: 'none', color: '#1e293b', minWidth: 0 };
const buttonStyle = { backgroundColor: '#ee4d2d', color: '#fff', padding: '16px', borderRadius: '12px', border: 'none', fontSize: '14px', fontWeight: '800', cursor: 'pointer', transition: '0.2s', boxShadow: '0 4px 6px -1px rgba(238, 77, 45, 0.3)' };
const footerStyle = { textAlign: 'center', fontSize: '12px', color: '#94a3b8', margin: 0 };

export default LoginPage;
